/**
 * Admin Panel Component
 * 
 * Admin-only panel for managing teams and accounts.
 * Features:
 * - View all teams with their accounts
 * - Create new teams
 * - Add accounts to existing teams
 * - Remove dynamically added accounts
 * 
 * IMPORTANT: Only visible to admin users (isAdmin: true)
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const AdminPanel = ({ isAdmin, userEmail }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // New team form state (with SSO details)
  const [newTeam, setNewTeam] = useState({
    teamName: '',              // Required - Team Name
    displayName: '',           // Required - Display Name shown in sidebar
    defaultRegion: 'eu-central-1', // Default region for accounts
    // SSO Configuration
    azureAdGroupId: '',        // Azure AD Group Object ID for team access
    azureAdGroupName: ''       // Azure AD Group Display Name
  });

  // Generate Team ID from Team Name (remove spaces, special chars, convert to kebab-case)
  const generateTeamId = (teamName) => {
    return teamName
      .trim()
      .replace(/[^a-zA-Z0-9\s-]/g, '')  // Remove special characters
      .replace(/\s+/g, '-')              // Replace spaces with hyphens
      .replace(/-+/g, '-')               // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');            // Remove leading/trailing hyphens
  };

  // New account form state
  const [newAccount, setNewAccount] = useState({
    accountId: '',
    environment: 'dev'
  });

  // Available environments for account creation
  const ENVIRONMENTS = [
    { value: 'prod', label: 'Production (prod)' },
    { value: 'dev', label: 'Development (dev)' },
    { value: 'qa', label: 'QA (qa)' },
    { value: 'acc', label: 'Acceptance (acc)' },
    { value: 'shared', label: 'Shared (shared)' },
    { value: 'sandbox', label: 'Sandbox (sandbox)' }
  ];

  // Available regions (EU only)
  const REGIONS = [
    { value: 'eu-central-1', label: 'EU (Frankfurt) - eu-central-1' },
    { value: 'eu-west-1', label: 'EU (Ireland) - eu-west-1' }
  ];

  // Fetch teams from backend API
  const fetchTeams = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Use axios which has auth interceptor configured
      const axios = (await import('axios')).default;
      const response = await axios.get(`${API_URL}/api/admin/teams`);
      setTeams(response.data.teams || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Create team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    if (!newTeam.teamName) {
      alert('Team Name is required');
      return;
    }

    if (!newTeam.displayName) {
      alert('Display Name is required');
      return;
    }

    if (!newTeam.azureAdGroupId) {
      alert('Azure AD Group ID is required for SSO access');
      return;
    }

    // Generate Team ID from Team Name
    const teamId = generateTeamId(newTeam.teamName);
    
    if (!teamId) {
      alert('Team Name must contain at least one letter or number');
      return;
    }

    // Check if team already exists
    if (teams.some(t => t.teamId === teamId)) {
      alert(`Team with ID "${teamId}" already exists. Please choose a different Team Name.`);
      return;
    }
    
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${API_URL}/api/admin/teams`, {
        teamId: teamId,
        teamName: newTeam.teamName,
        displayName: newTeam.displayName,
        defaultRegion: newTeam.defaultRegion,
        accounts: [],
        // SSO Configuration
        ssoConfig: {
          azureAdGroupId: newTeam.azureAdGroupId,
          azureAdGroupName: newTeam.azureAdGroupName || newTeam.teamName
        }
      });
      
      setSuccessMessage(`Team "${newTeam.displayName}" created successfully!`);
      setShowAddTeamModal(false);
      setNewTeam({ 
        teamName: '', 
        displayName: '',
        defaultRegion: 'eu-central-1',
        azureAdGroupId: '',
        azureAdGroupName: ''
      });
      fetchTeams();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Add account to team
  const handleAddAccount = async (e) => {
    e.preventDefault();
    
    if (!newAccount.accountId || !selectedTeam) {
      alert('Account ID is required');
      return;
    }

    // Validate account ID (should be 12 digits)
    if (newAccount.accountId.length !== 12) {
      alert('AWS Account ID must be exactly 12 digits');
      return;
    }

    // Check if account already exists in this team
    if (selectedTeam.accounts?.some(a => a.accountId === newAccount.accountId)) {
      alert(`Account "${newAccount.accountId}" already exists in ${selectedTeam.teamId}`);
      return;
    }
    
    try {
      const axios = (await import('axios')).default;
      // Use team's default region
      const accountData = {
        accountId: newAccount.accountId,
        environment: newAccount.environment,
        region: selectedTeam.defaultRegion || 'eu-central-1'
      };
      await axios.post(`${API_URL}/api/admin/teams/${selectedTeam.teamId}/accounts`, accountData);
      
      setSuccessMessage(`Account "${newAccount.accountId}" added to ${selectedTeam.teamId}!`);
      setShowAddAccountModal(false);
      setNewAccount({ accountId: '', environment: 'dev' });
      setSelectedTeam(null);
      fetchTeams();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Remove account from team
  const handleRemoveAccount = async (teamId, accountId, source, account) => {
    // For static config accounts, show info message
    if (source === 'config' && !account?.addedBy) {
      alert('Cannot remove accounts from static configuration.\nThis account is defined in account_config.py');
      return;
    }
    
    if (!window.confirm(`Remove account ${accountId} from team ${teamId}?`)) {
      return;
    }
    
    try {
      const axios = (await import('axios')).default;
      await axios.delete(`${API_URL}/api/admin/teams/${teamId}/accounts/${accountId}`);
      
      setSuccessMessage(`Account "${accountId}" removed from ${teamId}!`);
      fetchTeams();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Delete team (only dynamic teams can be deleted)
  const handleDeleteTeam = async (teamId, source) => {
    if (source === 'config') {
      alert('Cannot delete teams from static configuration.\nThis team is defined in account_config.py');
      return;
    }
    
    if (!window.confirm(`Delete team "${teamId}"? This will remove all dynamically added accounts.`)) {
      return;
    }
    
    try {
      const axios = (await import('axios')).default;
      await axios.delete(`${API_URL}/api/admin/teams/${teamId}`);
      
      setSuccessMessage(`Team "${teamId}" deleted!`);
      fetchTeams();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  // Don't render if not admin
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>🔧 Admin Panel</h2>
        <p className="admin-subtitle">Manage teams and AWS accounts</p>
      </div>

      {successMessage && (
        <div className="success-banner">
          ✅ {successMessage}
        </div>
      )}

      {error && (
        <div className="error-banner">
          ⚠️ {error}
        </div>
      )}

      <div className="admin-actions">
        <button 
          className="admin-btn primary"
          onClick={() => setShowAddTeamModal(true)}
        >
          ➕ Add New Team
        </button>
        <button 
          className="admin-btn secondary"
          onClick={fetchTeams}
        >
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="spinner"></div>
          <span>Loading teams...</span>
        </div>
      ) : (
        <div className="teams-grid">
          {teams.map(team => (
            <div key={team.teamId} className="team-card">
              <div className="team-card-header">
                <h3>👥 {team.displayName}</h3>
              </div>
              
              <div className="team-id">ID: {team.teamId}</div>
              
              <div className="accounts-section">
                <div className="accounts-header">
                  <h4>Accounts ({team.accounts?.length || 0})</h4>
                  <button 
                    className="add-account-btn"
                    onClick={() => {
                      setSelectedTeam(team);
                      setShowAddAccountModal(true);
                    }}
                    title="Add account to this team"
                  >
                    ➕
                  </button>
                </div>
                
                <div className="accounts-list">
                  {team.accounts?.map(account => (
                    <div key={account.accountId} className="account-item">
                      <div className="account-info">
                        <span className={`env-badge env-${account.environment}`}>
                          {account.environment?.toUpperCase()}
                        </span>
                        <span className="account-id">{account.accountId}</span>
                      </div>
                      {account.addedBy && (
                        <button 
                          className="remove-btn"
                          onClick={() => handleRemoveAccount(team.teamId, account.accountId, team.source, account)}
                          title="Remove this account"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  
                  {(!team.accounts || team.accounts.length === 0) && (
                    <div className="no-accounts">No accounts configured</div>
                  )}
                </div>
              </div>
              
              {team.createdBy && (
                <div className="team-meta">
                  <span>Created by: {team.createdBy}</span>
                  {team.source === 'dynamodb' && (
                    <button 
                      className="delete-team-btn"
                      onClick={() => handleDeleteTeam(team.teamId, team.source)}
                      title="Delete this team"
                      style={{
                        marginLeft: '12px',
                        padding: '4px 8px',
                        background: 'transparent',
                        border: '1px solid #DC2626',
                        color: '#DC2626',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Delete Team
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className="modal-overlay" onClick={() => setShowAddTeamModal(false)}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Add New Team</h3>
              <button className="modal-close" onClick={() => setShowAddTeamModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateTeam}>
              <div className="form-section">
                <h4>Team Information</h4>
                
                <div className="form-group">
                  <label>Team Name *</label>
                  <input
                    type="text"
                    value={newTeam.teamName}
                    onChange={e => setNewTeam({...newTeam, teamName: e.target.value})}
                    placeholder="e.g., New Team"
                    required
                  />
                  <small>Internal team name (ID will be auto-generated: "{generateTeamId(newTeam.teamName) || '...'}")</small>
                </div>
                
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={newTeam.displayName}
                    onChange={e => setNewTeam({...newTeam, displayName: e.target.value})}
                    placeholder="e.g., New Team Display Name"
                    required
                  />
                  <small>Name shown in the dashboard sidebar</small>
                </div>

                <div className="form-group">
                  <label>Default Region</label>
                  <select
                    value={newTeam.defaultRegion}
                    onChange={e => setNewTeam({...newTeam, defaultRegion: e.target.value})}
                  >
                    {REGIONS.map(region => (
                      <option key={region.value} value={region.value}>{region.label}</option>
                    ))}
                  </select>
                  <small>Default AWS region for accounts in this team</small>
                </div>
              </div>

              <div className="form-section">
                <h4>🔐 SSO Configuration (Azure AD)</h4>
                
                <div className="form-group">
                  <label>Azure AD Group ID *</label>
                  <input
                    type="text"
                    value={newTeam.azureAdGroupId}
                    onChange={e => setNewTeam({...newTeam, azureAdGroupId: e.target.value.trim()})}
                    placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                    required
                  />
                  <small>Object ID of the Azure AD group (from Azure Portal → Groups → Object ID)</small>
                </div>
                
                <div className="form-group">
                  <label>Azure AD Group Name</label>
                  <input
                    type="text"
                    value={newTeam.azureAdGroupName}
                    onChange={e => setNewTeam({...newTeam, azureAdGroupName: e.target.value})}
                    placeholder="e.g., iCOP-NewTeam-Users"
                  />
                  <small>Display name of the Azure AD group for documentation</small>
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setShowAddTeamModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Create Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Account Modal */}
      {showAddAccountModal && selectedTeam && (
        <div className="modal-overlay" onClick={() => setShowAddAccountModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Add Account to {selectedTeam.displayName}</h3>
              <button className="modal-close" onClick={() => setShowAddAccountModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleAddAccount}>
              <div className="form-group">
                <label>AWS Account ID *</label>
                <input
                  type="text"
                  value={newAccount.accountId}
                  onChange={e => setNewAccount({...newAccount, accountId: e.target.value.replace(/\D/g, '')})}
                  placeholder="e.g., 123456789012"
                  pattern="[0-9]{12}"
                  maxLength="12"
                  required
                />
                <small>12-digit AWS account number</small>
              </div>
              
              <div className="form-group">
                <label>Environment *</label>
                <select
                  value={newAccount.environment}
                  onChange={e => setNewAccount({...newAccount, environment: e.target.value})}
                  required
                >
                  {ENVIRONMENTS.map(env => (
                    <option key={env.value} value={env.value}>{env.label}</option>
                  ))}
                </select>
                <small>Region: {selectedTeam.defaultRegion || 'eu-central-1'} (team default)</small>
              </div>
              
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setShowAddAccountModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  Add Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
